import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SPONSORSHIP_QUERY_KEYS } from "@/features/sponsorship/constants/queryKeys";
import { sponsorshipService } from "@/features/sponsorship/services/sponsorship.service";
import type { SponsorshipTerm } from "@/features/sponsorship/types/sponsorship.type";

type UseSponsorshipTermsOptions = { enabled?: boolean };

// Règlement versionné servi par l'API. queryFn résout null (au lieu de
// rejeter) quand l'endpoint est absent ou en erreur : le composant retombe
// alors sur le contenu statique embarqué — le dialog Règlement fonctionne
// dans tous les cas.
export const useSponsorshipTerms = ({ enabled = true }: UseSponsorshipTermsOptions = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<SponsorshipTerm | null>({
    queryKey: SPONSORSHIP_QUERY_KEYS.terms(),
    queryFn: async () => {
      try {
        const response = await sponsorshipService.getCurrentTerms();
        return response.sponsorship_term;
      }
      catch {
        return null;
      }
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const acceptMutation = useMutation({
    mutationFn: (termId: string) => sponsorshipService.acceptTerms(termId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPONSORSHIP_QUERY_KEYS.terms() });
    },
  });

  return {
    term: data ?? null,
    isLoading,
    acceptTerms: acceptMutation.mutateAsync,
    isAccepting: acceptMutation.isPending,
  };
};
